/**
 * Drag-handle for SidePanel resize.
 *
 * Visual: 12px-wide hit area pinned to the panel's left edge with a
 * centered always-visible grip indicator (3 dots vertically). The
 * grip is the discoverable affordance; the wider hit area makes
 * grabbing the edge with a mouse comfortable, and stays usable on
 * touch devices where ~18 CSS pixels (12px @1.5x) is adequate for a
 * power-user action.
 *
 * On hover / keyboard focus the dots turn brand-coloured and a 1px
 * full-height ghost rule appears in the strip. Cursor is `ew-resize`
 * across the entire hit area including the grip itself.
 *
 * The handle is focusable (tabIndex=0) so keyboard users can resize
 * via the bound onKeyDown handler. ARIA `role="separator"` +
 * `aria-orientation` + `aria-valuemin/max/now` describe it as a
 * resize control to assistive tech.
 */
import { styled } from '../styled';

const GRIP_CLASS = 'qhs-side-panel-resize-grip';

const HitArea = styled('div')({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 12,
  cursor: 'ew-resize !important',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // ::before is the full-height ghost rule (subtle on idle, brand on hover).
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 5,
    top: 0,
    bottom: 0,
    width: 1,
    background: 'transparent',
    transition: 'background-color 160ms ease, box-shadow 160ms ease',
    pointerEvents: 'none',
  },
  '&:hover::before, &:focus-visible::before': {
    background: 'var(--qhs-brand)',
    boxShadow: '0 0 0 1px var(--qhs-focus-ring)',
  },
  '&:focus-visible': {
    outline: 'none',
  },
  [`&:hover .${GRIP_CLASS} span, &:focus-visible .${GRIP_CLASS} span`]: {
    background: 'var(--qhs-brand)',
    transform: 'scale(1.15)',
  },
});

const Grip = styled('div')({
  width: 4,
  height: 44,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  zIndex: 1,
  // Inherit cursor + let pointer events bubble to HitArea.
  cursor: 'ew-resize !important',
  '& span': {
    display: 'block',
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: 'var(--qhs-text-3)',
    transition: 'background-color 160ms ease, transform 160ms ease',
    pointerEvents: 'none',
  },
});

const ResizeHandle = ({ onPointerDown, onKeyDown, width, minWidth, maxWidth, ariaLabel }) => (
  <HitArea
    role="separator"
    aria-orientation="vertical"
    aria-label={ariaLabel || 'Resize panel'}
    aria-valuemin={minWidth}
    aria-valuemax={maxWidth}
    aria-valuenow={width}
    tabIndex={0}
    onPointerDown={onPointerDown}
    onKeyDown={onKeyDown}
    title={ariaLabel || 'Drag to resize'}
  >
    <Grip className={GRIP_CLASS}>
      <span />
      <span />
      <span />
    </Grip>
  </HitArea>
);

export default ResizeHandle;
